import type { Granularity } from '@zelisight/contract';

/**
 * The shared date range, plan FR-DASH-2.
 *
 * Granularity auto selects hour at seven days or less and day beyond it. That
 * is not cosmetic: the hourly counter partitions carry a 90 day TTL and are far
 * more numerous, so a 90 day hourly query is both slower and, past the TTL,
 * wrong. One rule, applied in one place.
 */

export const RANGE_PRESETS = ['today', '24h', '7d', '30d', '90d', 'custom'] as const;
export type RangePreset = (typeof RANGE_PRESETS)[number];

export const DEFAULT_PRESET: RangePreset = '7d';

export const RANGE_LABELS: Record<RangePreset, string> = {
  today: 'Today',
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  custom: 'Custom',
};

export interface ResolvedRange {
  preset: RangePreset;
  /** Inclusive lower bound, ISO-8601 with a timezone. */
  from: string;
  /** Exclusive upper bound, ISO-8601 with a timezone. */
  to: string;
  granularity: Granularity;
}

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/**
 * Two days or less gets hourly buckets.
 *
 * This was seven days, and seven days was the one preset that could not be
 * served. The read budget that matters here is the HyperLogLog union, and it
 * scales with buckets multiplied by the top rows a tile asks for: the Overview
 * wants ten. At hourly resolution seven days is 168 buckets, so 168 plus 168
 * times 10, about 1,850 sketches, against a budget of 1,200. Thirty days at
 * daily resolution is 30 plus 300, about 330. So "last 7 days" was refused with
 * "narrow this range" while the wider "last 30 days" answered instantly, which
 * is exactly as backwards as it sounds.
 *
 * Two days is where the arithmetic stays comfortable, 48 plus 480, and it is
 * also the honest product answer: a seven day chart drawn at 168 points is
 * noise, and every comparable tool draws that week as days.
 *
 * Raising the budget instead would have been treating the symptom. The cost is
 * real, and it is paid on a customer's table.
 */
export const HOURLY_MAX_MS = 2 * DAY_MS;

/** Exact unique counts are only offered where they are cheap, plan FR-DASH-16. */
export const EXACT_UNIQUES_MAX_MS = 7 * DAY_MS;

/**
 * The viewer's IANA timezone, or an empty string if the browser will not say.
 *
 * Sent to `/v1/me` so a newly created org reports in the zone of the person
 * creating it. Never used to decide what a tenant may see: it is a preference,
 * and the server validates it before storing it.
 */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    // A browser without a usable Intl implementation is not a reason to fail a
    // sign in. The server falls back to UTC, which is what happens today.
    return '';
  }
}

export function granularityFor(fromMs: number, toMs: number): Granularity {
  return toMs - fromMs <= HOURLY_MAX_MS ? 'hour' : 'day';
}

export function exactUniquesAvailable(range: ResolvedRange): boolean {
  return Date.parse(range.to) - Date.parse(range.from) <= EXACT_UNIQUES_MAX_MS;
}

/**
 * Start of the current day in a named IANA timezone, expressed as a UTC instant.
 *
 * The org's reporting timezone decides which day "today" is (plan D30), and a
 * fixed offset would be wrong for half the year in every zone with daylight
 * saving. `Intl.DateTimeFormat` with `timeZone` is the only correct source of
 * that offset, so it is what is used here rather than arithmetic on hours.
 */
export function startOfDayInZone(now: Date, timeZone: string): Date {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(now);

  const get = (type: Intl.DateTimeFormatPartTypes): number => {
    const part = parts.find((p) => p.type === type);
    return part ? Number(part.value) : 0;
  };

  // `hour` can format as 24 for midnight under hour12: false in some engines.
  const hour = get('hour') % 24;
  const elapsedMs =
    hour * HOUR_MS + get('minute') * 60 * 1000 + get('second') * 1000 + now.getMilliseconds();

  return new Date(now.getTime() - elapsedMs);
}

export interface CustomBounds {
  /** `YYYY-MM-DD`, inclusive. */
  fromDate: string;
  /** `YYYY-MM-DD`, inclusive: resolved to the end of that day. */
  toDate: string;
}

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isDateOnly(value: string): boolean {
  if (!DATE_ONLY_RE.test(value)) return false;
  return Number.isFinite(Date.parse(`${value}T00:00:00Z`));
}

/**
 * Turn a preset (or explicit custom bounds) into the exact instants the read
 * API is asked for. `now` and `timeZone` are parameters rather than ambient
 * state so this is testable without freezing the clock.
 */
export function resolveRange(
  preset: RangePreset,
  timeZone: string,
  now: Date,
  custom?: CustomBounds,
): ResolvedRange {
  const toMs = now.getTime();

  if (preset === 'custom') {
    if (!custom || !isDateOnly(custom.fromDate) || !isDateOnly(custom.toDate)) {
      // A malformed custom range in a shared URL falls back to the default
      // rather than sending the API two NaNs.
      return resolveRange(DEFAULT_PRESET, timeZone, now);
    }
    const startOfFrom = startOfDayInZone(new Date(`${custom.fromDate}T12:00:00Z`), timeZone);
    const startOfToDay = startOfDayInZone(new Date(`${custom.toDate}T12:00:00Z`), timeZone);
    const endOfTo = new Date(startOfToDay.getTime() + DAY_MS);
    if (endOfTo.getTime() <= startOfFrom.getTime()) {
      return resolveRange(DEFAULT_PRESET, timeZone, now);
    }
    return {
      preset,
      from: startOfFrom.toISOString(),
      to: endOfTo.toISOString(),
      granularity: granularityFor(startOfFrom.getTime(), endOfTo.getTime()),
    };
  }

  let fromMs: number;
  switch (preset) {
    case 'today':
      fromMs = startOfDayInZone(now, timeZone).getTime();
      break;
    case '24h':
      fromMs = toMs - DAY_MS;
      break;
    case '7d':
      fromMs = toMs - 7 * DAY_MS;
      break;
    case '30d':
      fromMs = toMs - 30 * DAY_MS;
      break;
    case '90d':
      fromMs = toMs - 90 * DAY_MS;
      break;
  }

  return {
    preset,
    from: new Date(fromMs).toISOString(),
    to: new Date(toMs).toISOString(),
    granularity: granularityFor(fromMs, toMs),
  };
}

export function isRangePreset(value: string | null): value is RangePreset {
  return value !== null && (RANGE_PRESETS as readonly string[]).includes(value);
}
